import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import { _notNil } from '@/littledash';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { successToast } from '@/helpers';
import cn from 'classnames';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';

interface CreateStudyFormData {
  name: string;
}

interface CreateStudyFormProps {
  closeModal: () => void;
  onAddTemplate: (data: unknown) => void;
}

const CreateStudyForm: FC<CreateStudyFormProps> = ({ closeModal, onAddTemplate }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<unknown>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateStudyFormData>();

  const onSubmit = async (formData: CreateStudyFormData): Promise<void> => {
    setSubmitting(true);
    setApiError(null);
    try {
      const {
        data: { data },
      } = await Http.post(apiRoute('team.studies.forms.create'), { ...formData });
      closeModal();
      successToast('Created new template');
      onAddTemplate(data);
    } catch (error) {
      setApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ui-card mw6 pa4 center" data-test-component="CreateStudyForm">
      <h2 className="f4 lh-title normal pb3" data-testid="create-study-form__title">
        Create a new form
      </h2>
      {_notNil(apiError) && <ApiErrorBanner error={apiError} />}
      <form onSubmit={handleSubmit(onSubmit)} data-test-element="form">
        <div>
          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            autoFocus
            style={{ marginBottom: 0 }}
            className={cn({ input__error: _notNil(errors?.name) })}
            data-testid="create-study-form__name-input"
            {...register('name', {
              required: 'This field is required',
              minLength: { value: 3, message: 'Minimum of 3 characters' },
              maxLength: { value: 255, message: 'Maximum of 255 characters' },
            })}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => (
              <p className="f6 red db pt2" data-testid="create-study-form__name-error">
                {message}
              </p>
            )}
          />
        </div>
        <div className="pt4">
          <Button submit className="mr3" disabled={submitting} testId="create-study-form__submit">
            Save and continue
          </Button>
          <Button plain onClick={closeModal} testId="create-study-form__cancel">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateStudyForm;
